import React, { useState, useEffect } from 'react';
import { Heart, Repeat, MessageCircle, UserPlus, Bell, CheckCheck } from 'lucide-react';
import { NotificationItem, User } from '../../types';
import { api } from '../../services/api';
import styles from './Notifications.module.css';

interface NotificationListProps {
  currentUser: User | null;
}

export const NotificationList: React.FC<NotificationListProps> = ({ currentUser }) => {
  const [notifs, setNotifs] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadNotifs();
    }
  }, [currentUser]);

  const loadNotifs = async () => {
    try {
      const res = await api.getNotifications();
      setNotifs(res.notifications);
    } catch {
      setNotifs([
        {
          id: 'n1',
          recipient_id: currentUser?.id || 'usr_demo',
          sender: {
            id: 'usr_cordle_official',
            username: 'cordle',
            email: '',
            display_name: 'Cordle Team',
            bio: '',
            avatar_url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&auto=format&fit=crop&q=80',
            banner_url: '',
            custom_theme: 'neon-indigo',
            pronouns: '',
            location: '',
            website: '',
            social_links: {},
            verified: true,
            status: 'online',
            custom_status_text: '',
            is_email_verified: true,
            two_factor_enabled: false,
            created_at: Date.now(),
          },
          type: 'mention',
          target_id: 'post_welcome',
          message_preview: 'Welcome to Cordle! Your account is active and verified.',
          is_read: false,
          created_at: Date.now() - 3600000,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {}
  };

  const renderIcon = (type: string) => {
    switch (type) {
      case 'like': return <Heart size={18} color="#f43f5e" fill="currentColor" />;
      case 'repost': return <Repeat size={18} color="#10b981" />;
      case 'comment': return <MessageCircle size={18} color="var(--accent-primary)" />;
      case 'follow': return <UserPlus size={18} color="var(--accent-primary)" />;
      default: return <Bell size={18} color="var(--accent-primary)" />;
    }
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Notifications</h2>
        <button
          onClick={handleMarkAllRead}
          style={{
            fontSize: '0.85rem',
            color: 'var(--accent-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
          }}
        >
          <CheckCheck size={16} /> Mark all read
        </button>
      </div>

      {notifs.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
          No notifications yet!
        </div>
      ) : (
        notifs.map((item) => (
          <div key={item.id} className={`${styles.item} ${!item.is_read ? styles.unread : ''}`}>
            <div>{renderIcon(item.type)}</div>
            <img src={item.sender.avatar_url} alt={item.sender.display_name} className={styles.avatar} />
            <div className={styles.text}>
              <strong>{item.sender.display_name}</strong> {item.message_preview}
            </div>
            <span className={styles.time}>{new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        ))
      )}
    </div>
  );
};
